import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Button from '../../components/Button';
import { UserIdSearch } from '../../components/UserIdSearch';
import { UserLinks } from '../../components/UserLinks';
import { useAuth } from '../../hooks/useAuth';
import { StreakScenarios } from './StreakScenarios';
import { cardClass, inputClass, StreakStateEditor } from './StreakStateEditor';
import { streakService, type ActionType, type StreakState, type StreakTarget } from './streakService';

const StreakWorkspace = ({ userId, timezone }: StreakTarget) => {
    const [state, setState] = useState<StreakState | null>(null);
    const [attempt, setAttempt] = useState(0);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [message, setMessage] = useState<string | null>(null);
    const [actionType, setActionType] = useState<ActionType>('triggerLogged');
    const [redo, setRedo] = useState(false);
    const mounted = useRef(false);
    const operationPending = useRef(false);

    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);
    useEffect(() => {
        let cancelled = false;
        setState(null);
        setError(null);
        streakService.get({ userId, timezone }).then(result => {
            if (!cancelled) setState(result);
        }).catch(cause => {
            if (!cancelled) setError(cause instanceof Error ? cause.message : 'Could not load streak state.');
        });
        return () => { cancelled = true; };
    }, [userId, timezone, attempt]);

    const run = async (action: () => Promise<void>, success: string, refresh = true) => {
        if (operationPending.current) return;
        operationPending.current = true;
        setBusy(true); setError(null); setMessage(null);
        try {
            await action();
            if (!mounted.current) return;
            setMessage(success);
            if (refresh) setAttempt(value => value + 1);
        } catch (cause) {
            if (mounted.current) setError(cause instanceof Error ? cause.message : 'The streak operation failed.');
        } finally {
            operationPending.current = false;
            if (mounted.current) setBusy(false);
        }
    };
    const disabled = busy || !state?.canWrite;

    return <div className="space-y-4">
        <div className="flex items-center gap-3">
            <Button variant="outline" disabled={busy} onClick={() => { setMessage(null); setAttempt(value => value + 1); }}>Refresh state</Button>
            <span className="text-sm text-gray-600">Reopen streak details in the app after making changes.</span>
        </div>
        {error && <p role="alert" className="rounded bg-red-50 p-3 text-red-700">{error}</p>}
        {message && <p role="status" className="rounded bg-green-50 p-3 text-green-800">{message}</p>}
        {!state && !error && <p>Loading streak state…</p>}
        {state && <>
            {!state.canWrite && <p className="rounded bg-amber-50 p-3 text-amber-800">Inspection only. Streak test changes are enabled in staging.</p>}
            <section className={cardClass}>
                <h2 className="mb-3 text-xl font-semibold">Current state</h2>
                <p className="mb-3 text-sm text-gray-600">Effective day: {state.dayKey} · {timezone} · day starts at 4 AM</p>
                <dl className="grid grid-cols-2 gap-4 md:grid-cols-4">
                    {Object.entries({ 'Displayed streak': state.displayedStreak, 'Stored streak': state.streak.current,
                        'Longest streak': state.streak.longest, 'Active days': state.activeDaysTotal,
                        'Available freezes': state.streakFreezes.available, 'Freeze progress': `${state.streakFreezes.progressDays}/3`,
                        'Run start': state.streak.runStartDay ?? 'None', 'Protected through': state.streak.protectedThrough ?? 'None',
                        'Last active day': state.streak.lastDay ?? 'None', 'Last recorded timezone': state.streak.lastTz ?? 'None',
                    }).map(([label, value]) => <div key={label}><dt className="text-xs text-gray-500">{label}</dt><dd className="break-words font-semibold">{value}</dd></div>)}
                </dl>
                <p className="mt-3 text-sm text-gray-600">Journey completions: {state.actionCounts.journeyItemCompleted} · Chat engagements: {state.actionCounts.ollieChatEngagement} · Triggers logged: {state.actionCounts.triggerLogged}</p>
            </section>
            <section className={cardClass}>
                <h2 className="mb-3 text-xl font-semibold">Consumed freezes</h2>
                <p className="break-words text-sm">Latest frozen dates: {state.frozenDays.join(', ') || 'None'}</p>
                <p className="mt-2 break-words text-sm">Pending notification: {state.freezeNotification
                    ? `${state.freezeNotification.streak}-day streak, ${state.freezeNotification.count} freezes (${state.freezeNotification.id})` : 'None'}</p>
            </section>
            <StreakStateEditor key={`editor-${state.revision}`} state={state} disabled={disabled} onSave={patch => run(async () => {
                await streakService.update({ userId, timezone, expectedRevision: state.revision, ...patch });
            }, 'Streak state saved.')} />
            <section className={cardClass}>
                <h2 className="mb-3 text-xl font-semibold">Record a test action today</h2>
                <fieldset disabled={disabled} className="space-y-3">
                    <label className="block text-sm">Action
                        <select className={inputClass} value={actionType} onChange={e => { setActionType(e.target.value as ActionType); setRedo(false); }}>
                            <option value="triggerLogged">Log a trigger</option><option value="journeyItemCompleted">Complete a journey item</option><option value="ollieChatEngagement">Engage with Ollie</option>
                        </select>
                    </label>
                    {actionType === 'journeyItemCompleted' && <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={redo} onChange={e => setRedo(e.target.checked)} />Journey redo (no completion counter increment)</label>}
                    <p className="text-sm text-gray-600">Uses the real streak and freeze logic. Repeating the same action tests duplicate handling. This records test activity only; it does not create a trigger log, journey item, or conversation.</p>
                    <Button disabled={disabled} onClick={() => void run(async () => {
                        await streakService.recordAction({ userId, timezone, type: actionType, countsOccurrence: !redo });
                    }, 'Test action recorded. Streak and freeze values refreshed.')}>Record test action</Button>
                </fieldset>
            </section>
            <StreakScenarios key={`scenario-${state.revision}`} target={{ userId, timezone }} disabled={disabled} run={run} />
            <section className={cardClass}>
                <h2 className="mb-3 text-xl font-semibold">Recorded activity days</h2>
                {state.days.length === 0 ? <p className="text-sm text-gray-500">No activity recorded.</p> : <div className="max-h-96 overflow-auto">
                    <table className="w-full text-left text-sm"><thead><tr><th className="p-2">Day</th><th className="p-2">Activities</th><th className="p-2">Streak length then</th></tr></thead>
                        <tbody>{state.days.map(day => <tr key={day.day} className="border-t border-gray-100"><td className="whitespace-nowrap p-2">{day.day}</td><td className="p-2">{day.categories.join(', ')}</td><td className="p-2">{day.streakDay ?? '—'}</td></tr>)}</tbody>
                    </table>
                </div>}
                {state.hasMoreDays && <p className="mt-2 text-sm text-gray-500">Showing the latest 120 active days.</p>}
            </section>
        </>}
    </div>;
};

export const Streaks = () => {
    const { user } = useAuth();
    const [params, setParams] = useSearchParams();
    const userId = params.get('userId') || user?.uid;
    const [timezone, setTimezone] = useState(() => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');
    const [timezoneInput, setTimezoneInput] = useState(timezone);
    return <main className="min-h-screen bg-gray-50 py-8">
        <div className="mx-auto max-w-7xl space-y-4 px-4 sm:px-6">
            <h1 className="text-3xl font-bold text-gray-900">Streaks</h1>
            <section className={cardClass}>
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <div><p className="text-sm text-gray-500">Viewing streak state for</p><p className="break-all font-mono text-sm">{userId}</p></div>
                    <div className="flex flex-wrap gap-2">{userId && <UserLinks userId={userId} />}
                        {userId !== user?.uid && <Button variant="outline" onClick={() => setParams({})}>Back to my streak</Button>}
                    </div>
                </div>
                <UserIdSearch onSearchResult={async found => { if (found) setParams({ userId: found.uid }); }} />
                <form className="flex flex-wrap items-end gap-2" onSubmit={e => { e.preventDefault(); setTimezone(timezoneInput.trim()); }}>
                    <label className="text-sm">User timezone
                        <input className={inputClass} required value={timezoneInput} onChange={e => setTimezoneInput(e.target.value)} placeholder="Europe/Amsterdam" />
                    </label>
                    <Button type="submit" variant="outline">Use timezone</Button>
                </form>
            </section>
            {userId && <StreakWorkspace key={`${userId}:${timezone}`} userId={userId} timezone={timezone} />}
        </div>
    </main>;
};
